/**
 * LotoWheel Modal — Tab Pozos Comunes (suscripciones de jackpots compartidos)
 * Se extiende sobre LotowheelModule declarado en lotowheel.js
 */

import { LotowheelModule } from './lotowheel.js';
import { App, Api } from '../../../admin.js';
import { t } from '../../../services/i18n-admin.js';

Object.assign(LotowheelModule, {
    // ====================================================================
    // POZOS COMUNES — Panel de suscripciones en modal de sorteo
    // ====================================================================

    /**
     * Carga el panel de pozos comunes para un sorteo dado.
     */
    async loadPozoComunesPanel(sorteoId) {
        const list = document.getElementById('pozosComunesListSorteo');
        if (!list) return;

        // Siempre recargar desde API (los pozos disponibles pueden cambiar entre aperturas)
        try {
            const data = await Api.callAdmin('/admin/pozos-comunes', 'GET');
            this._pozosComunesDisponibles = Array.isArray(data) ? data : [];
        } catch (e) {
            this._pozosComunesDisponibles = [];
        }

        this._suscripcionesPozosComunes = [];
        if (sorteoId) {
            try {
                const subs = await Api.callAdmin(`/admin/sorteos/${sorteoId}/pozos-comunes`, 'GET');
                this._suscripcionesPozosComunes = Array.isArray(subs) ? subs : [];
            } catch (e) {
                // Sin suscripciones previas
            }
        }

        this._renderPozoComunesPanel();
    },

    _renderPozoComunesPanel() {
        const list = document.getElementById('pozosComunesListSorteo');
        if (!list) return;

        if (this._pozosComunesDisponibles.length === 0) {
            list.innerHTML =
                '<div style="text-align:center; padding:32px 16px; color:var(--text-muted);">' +
                '<div style="font-size:32px; margin-bottom:8px;">🎰</div>' +
                '<div style="font-size:15px; font-weight:500; margin-bottom:4px;">' + t('Sin jackpots disponibles') + '</div>' +
                '<div style="font-size:13px;">' + t('Crea un jackpot en la sección "Pozos Comunes" del menú lateral.') + '</div>' +
                '</div>';
            return;
        }

        let html = '';
        for (let i = 0; i < this._pozosComunesDisponibles.length; i++) {
            const p = this._pozosComunesDisponibles[i];
            const sus = this._suscripcionesPozosComunes.find(s => s.pozo_id === p.id);
            const isSuscrito = sus !== undefined;
            // porcentaje_aporte null = hereda el defecto configurado en el pozo
            const pct = isSuscrito && sus.porcentaje_aporte !== null ? sus.porcentaje_aporte : '';
            const paralizar = isSuscrito ? sus.paralizar_segundos || 0 : 0;

            // Progreso del bote: pozo_actual / pozo_umbral
            const progresoPct =
                p.pozo_umbral > 0 ? Math.min(100, Math.round((p.pozo_actual / p.pozo_umbral) * 100)) : 0;

            html += `
                <div id="pozoRow_${p.id}" style="
                    background:var(--bg-card, var(--bg-secondary));
                    border:1px solid ${isSuscrito ? 'var(--color-primary, #4f6ef7)' : 'var(--border-color)'};
                    border-radius:10px;
                    padding:16px;
                    margin-bottom:10px;
                    transition: border-color 0.15s;
                ">
                  <!-- Cabecera: checkbox + nombre + bote actual -->
                  <div style="display:flex; align-items:flex-start; gap:14px;">
                    <input type="checkbox" id="pozo_check_${p.id}" ${isSuscrito ? 'checked' : ''}
                      data-action="lotowheel:onPozoComunToggle" data-id="${p.id}"
                      style="width:18px; height:18px; cursor:pointer; flex-shrink:0; margin-top:2px; accent-color:var(--color-primary, #4f6ef7);">

                    <div style="flex:1; min-width:0;">
                      <!-- Nombre + bote -->
                      <div style="display:flex; align-items:baseline; justify-content:space-between; gap:8px; flex-wrap:wrap;">
                        <div style="font-size:15px; font-weight:600; color:var(--text-primary);">${p.nombre}</div>
                        <div style="font-size:14px; font-weight:700; color:var(--color-primary, #4f6ef7); white-space:nowrap;">
                          ${t('Bote: ')}${App.formatMoney(p.pozo_actual, p.moneda)}
                        </div>
                      </div>

                      <!-- Barra de progreso hacia el umbral -->
                      <div style="margin:8px 0 4px;">
                        <div style="background:var(--border-color); border-radius:4px; height:6px; overflow:hidden;">
                          <div style="height:100%; width:${progresoPct}%; background:var(--color-primary, #4f6ef7); border-radius:4px; transition:width 0.3s;"></div>
                        </div>
                        <div style="font-size:12px; color:var(--text-muted); margin-top:4px;">
                          ${progresoPct}${t('% del umbral de disparo (')}${App.formatMoney(p.pozo_umbral, p.moneda)})
                        </div>
                      </div>

                      <!-- Controles: solo visibles si suscrito -->
                      <div style="display:flex; gap:16px; flex-wrap:wrap; margin-top:12px; padding-top:10px; border-top:1px solid var(--border-color); ${isSuscrito ? '' : 'opacity:0.4; pointer-events:none;'}">
                        <div>
                          <label style="display:block; font-size:12px; font-weight:500; color:var(--text-muted); margin-bottom:4px;">
                            ${t('% de cada apuesta que aporta')}
                          </label>
                          <input type="number" id="pozo_pct_${p.id}" value="${pct}"
                            placeholder="${p.porcentaje_aporte_defecto} (defecto)"
                            min="0.01" max="100" step="0.1"
                            class="form-input"
                            style="width:140px; font-size:14px; text-align:center;"
                            title="Dejar vacío para heredar el ${p.porcentaje_aporte_defecto}% configurado en el jackpot"
                            ${isSuscrito ? '' : 'disabled'}>
                        </div>
                        <div>
                          <label style="display:block; font-size:12px; font-weight:500; color:var(--text-muted); margin-bottom:4px;">
                            ${t('Pausa al disparar')}
                          </label>
                          <select id="pozo_paralizar_${p.id}" class="form-select"
                            style="width:140px; font-size:14px;"
                            ${isSuscrito ? '' : 'disabled'}>
                            <option value="0"   ${paralizar === 0 ? 'selected' : ''}>${t('Sin pausa')}</option>
                            <option value="15"  ${paralizar === 15 ? 'selected' : ''}>${t('15 segundos')}</option>
                            <option value="30"  ${paralizar === 30 ? 'selected' : ''}>${t('30 segundos')}</option>
                            <option value="60"  ${paralizar === 60 ? 'selected' : ''}>${t('1 minuto')}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>`;
        }
        list.innerHTML = html;
    },

    _onPozoComunToggle(pozoId, isChecked) {
        const pctInput = document.getElementById(`pozo_pct_${pozoId}`);
        const paralizarInput = document.getElementById(`pozo_paralizar_${pozoId}`);
        if (pctInput) pctInput.disabled = !isChecked;
        if (paralizarInput) paralizarInput.disabled = !isChecked;
        // Quitar/poner pointer-events:none en el container de controles
        const row = document.getElementById(`pozoRow_${pozoId}`);
        if (row) {
            const controls = row.querySelector('[style*="pointer-events"]');
            if (controls) {
                controls.style.opacity = isChecked ? '1' : '0.4';
                controls.style.pointerEvents = isChecked ? '' : 'none';
            }
            // Actualizar borde del card para feedback visual
            row.style.borderColor = isChecked ? 'var(--color-primary, #4f6ef7)' : 'var(--border-color)';
        }
    },

    async saveSuscripcionesPozosComunes() {
        const id = document.getElementById('lotowheelConfigId').value;
        if (!id) return;

        const suscripciones = [];
        for (let i = 0; i < this._pozosComunesDisponibles.length; i++) {
            const p = this._pozosComunesDisponibles[i];
            const check = document.getElementById(`pozo_check_${p.id}`);
            if (check && check.checked) {
                const pctInput = document.getElementById(`pozo_pct_${p.id}`);
                const paralizarInput = document.getElementById(`pozo_paralizar_${p.id}`);
                // Valor vacío = null (hereda defecto del pozo)
                const pctVal = pctInput?.value.trim();
                suscripciones.push({
                    pozo_id: p.id,
                    porcentaje_aporte: pctVal !== '' ? parseFloat(pctVal) || null : null,
                    paralizar_segundos: paralizarInput ? parseInt(paralizarInput.value) || 0 : 0,
                });
            }
        }

        const result = await Api.callAdmin(`/admin/sorteos/${id}/pozos-comunes`, 'PUT', { suscripciones });
        if (result !== null) {
            App.toast(t('Suscripciones guardadas: ') + suscripciones.length + t(' pozo(s) activo(s)'));
            this._suscripcionesPozosComunes = suscripciones.map(s => ({
                pozo_id: s.pozo_id,
                porcentaje_aporte: s.porcentaje_aporte,
                paralizar_segundos: s.paralizar_segundos,
            }));
        }
    },
});
